import { useSearchParams } from 'react-router-dom'
import { useState, useEffect, useCallback } from 'react'
import { PAGINATION } from '../constants/pagination'
import qs from 'qs'

function isEqualSafe(a, b) {
  if (
    typeof a !== 'object' ||
    typeof b !== 'object' ||
    a === null ||
    b === null
  ) {
    return a === b
  }

  const seen = new WeakSet()
  const replacer = (key, value) => {
    if (typeof value === 'object' && value !== null) {
      if (seen.has(value)) return
      seen.add(value)
    }
    if (key.startsWith('__reactFiber')) return
    return value
  }

  try {
    return JSON.stringify(a, replacer) === JSON.stringify(b, replacer)
  } catch (error) {
    console.warn('useUrlFilters: Erro ao comparar:', error)
    return false
  }
}

export default function useUrlFilters(defaultFilters = {}, namespace = '') {
  const [searchParams, setSearchParams] = useSearchParams()
  const prefix = namespace ? `${namespace}_` : ''

  const parseSearchParamsToFilters = useCallback(
    (params) => {
      const parsed = qs.parse(params.toString(), {
        ignoreQueryPrefix: true,
        arrayLimit: 0,
        depth: 10,
      })

      const filtersKey = `${prefix}filters`
      parsed[filtersKey] = parsed[filtersKey] || {}

      return parsed
    },
    [prefix]
  )

  const initialDataFromUrl = parseSearchParamsToFilters(searchParams)
  const filtersFromUrl = initialDataFromUrl[`${prefix}filters`] || {}
  const pageFromUrl = Number(
    initialDataFromUrl[`${prefix}page`] || PAGINATION.DEFAULT_PAGE
  )
  const perPageFromUrl = Number(
    initialDataFromUrl[`${prefix}perPage`] || PAGINATION.DEFAULT_PER_PAGE
  )

  const [filters, setFiltersState] = useState(() => ({
    ...defaultFilters,
    ...filtersFromUrl,
  }))

  const [pagination, setPaginationState] = useState(() => ({
    page: pageFromUrl,
    perPage: perPageFromUrl,
  }))

  const updateUrlParams = useCallback(
    (currentFiltersObject, currentPagination) => {
      const page = Number(currentPagination.page ?? PAGINATION.DEFAULT_PAGE)
      const perPage = Number(
        currentPagination.perPage ?? PAGINATION.DEFAULT_PER_PAGE
      )

      const filtersKey = `${prefix}filters`
      const pageKey = `${prefix}page`
      const perPageKey = `${prefix}perPage`

      const existingParams = qs.parse(searchParams.toString(), {
        ignoreQueryPrefix: true,
        arrayLimit: 0,
        depth: 10,
      })

      existingParams[filtersKey] = currentFiltersObject
      existingParams[pageKey] = page
      existingParams[perPageKey] = perPage

      const query = qs.stringify(existingParams, {
        encode: true,
        arrayFormat: 'indices', // <-- AQUI O AJUSTE
        skipNulls: false,
        depth: 10,
      })

      setSearchParams(query)
    },
    [searchParams, setSearchParams, prefix]
  )

  const applyFilters = useCallback(
    (newFilters) => {
      const resetPage = { ...pagination, page: 1 }
      setFiltersState(newFilters)
      setPaginationState(resetPage)
      updateUrlParams(newFilters, resetPage)
    },
    [pagination, updateUrlParams]
  )

  const clearFilters = useCallback(() => {
    const resetPage = { ...pagination, page: 1 }
    setFiltersState(defaultFilters)
    setPaginationState(resetPage)
    updateUrlParams(defaultFilters, resetPage)
  }, [defaultFilters, pagination, updateUrlParams])

  const changePage = useCallback(
    (newPageZeroBased) => {
      const newPage = newPageZeroBased + 1 // converte zero-based para 1-based
      setPagination((prev) => ({ ...prev, page: newPage }))
      updateUrlParams(filters, { ...pagination, page: newPage })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filters, updateUrlParams, pagination]
  )

  const setPagination = useCallback(
    (newPagination) => {
      const page = Number(newPagination.page ?? PAGINATION.DEFAULT_PAGE)
      const perPage = Number(
        newPagination.perPage ?? PAGINATION.DEFAULT_PER_PAGE
      )

      const updatedPagination = { page, perPage }
      setPaginationState(updatedPagination)
      updateUrlParams(filters, updatedPagination)
    },
    [filters, updateUrlParams]
  )

  useEffect(() => {
    const dataFromUrl = parseSearchParamsToFilters(searchParams)
    const newFilters = dataFromUrl[`${prefix}filters`] || {}

    const filtersToApply =
      Object.keys(newFilters).length === 0 ? defaultFilters : newFilters

    const newPagination = {
      page: Number(dataFromUrl[`${prefix}page`] || PAGINATION.DEFAULT_PAGE),
      perPage: Number(
        dataFromUrl[`${prefix}perPage`] || PAGINATION.DEFAULT_PER_PAGE
      ),
    }

    const filtersChanged = !isEqualSafe(filters, filtersToApply)
    const paginationChanged = !isEqualSafe(pagination, newPagination)

    if (filtersChanged) {
      setFiltersState(filtersToApply)
    }
    if (paginationChanged) {
      setPaginationState(newPagination)
    }
  }, [
    searchParams,
    parseSearchParamsToFilters,
    filters,
    pagination,
    prefix,
    defaultFilters,
  ])

  return {
    filters,
    setFilters: applyFilters,
    clearFilters,
    pagination,
    changePage,
    setPagination,
  }
}
